/*
 * Auto-translate the entire DOM into the user's chosen language on the fly.
 *
 * How it works:
 *   1. Walks every visible text node under document.body.
 *   2. Skips nodes inside <script>, <style>, <code>, <pre>, editable areas,
 *      inputs, and anything explicitly marked with `data-i18n-skip="true"`.
 *   3. Batches the source strings, calls POST /api/i18n/translate on the
 *      backend, which returns the LLM-generated translations (cached in Mongo
 *      so each string is only paid for once, ever).
 *   4. Also caches in localStorage so second visits are instant.
 *   5. Watches the DOM with MutationObserver — when React swaps a route or
 *      injects new content, only the fresh nodes get translated.
 */
import { useEffect, useRef } from "react";
import { useLang } from "@/context/LanguageContext";
import { API } from "@/lib/api";

const LS_PREFIX = "jm_i18n_";
const MAX_BATCH_STRINGS = 60;         // matches backend cap
const TRIM_SKIP_RE = /^[\s\d\.,%+\-–—:/×xX*|·•]+$/;    // pure punctuation / numbers
const MIN_CHARS = 2;

// Tags we never dive into.
const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "SVG", "PATH",
  "TEXTAREA", "INPUT", "IFRAME", "CANVAS", "VIDEO", "AUDIO",
]);

function loadCache(lang) {
  try {
    return new Map(Object.entries(JSON.parse(localStorage.getItem(LS_PREFIX + lang) || "{}")));
  } catch {
    return new Map();
  }
}
function saveCache(lang, map) {
  try {
    localStorage.setItem(LS_PREFIX + lang, JSON.stringify(Object.fromEntries(map)));
  } catch { /* quota — ignore */ }
}

function shouldSkipElement(el) {
  if (!el || !el.tagName) return true;
  if (SKIP_TAGS.has(el.tagName)) return true;
  if (el.isContentEditable) return true;
  if (el.dataset && (el.dataset.i18nSkip === "true" || el.getAttribute?.("translate") === "no")) return true;
  return false;
}

function shouldSkipText(txt) {
  if (!txt) return true;
  const t = txt.trim();
  if (t.length < MIN_CHARS) return true;
  if (TRIM_SKIP_RE.test(t)) return true;
  // Emails, URLs, ISO dates, standalone numbers.
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return true;
  if (/^https?:\/\//.test(t)) return true;
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return true;
  return false;
}

function collectTextNodes(root) {
  const nodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      if (shouldSkipElement(n.parentElement)) return NodeFilter.FILTER_REJECT;
      if (shouldSkipText(n.nodeValue)) return NodeFilter.FILTER_REJECT;
      // Skip anything already translated (we stamp a data attribute on the parent).
      let p = n.parentElement;
      while (p && p !== root) {
        if (p.dataset && p.dataset.i18nSkip === "true") return NodeFilter.FILTER_REJECT;
        p = p.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n;
  while ((n = walker.nextNode())) nodes.push(n);
  return nodes;
}

async function translateBatch(strings, lang) {
  try {
    const res = await fetch(`${API}/i18n/translate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ target: lang, strings }),
    });
    if (!res.ok) return {};
    const data = await res.json();
    return data.translations || {};
  } catch {
    return {};
  }
}

async function translateDom(lang, cache, applied) {
  if (!lang || lang === "en") return;

  const nodes = collectTextNodes(document.body);
  if (!nodes.length) return;

  // De-duplicate the source strings so we don't ship the same phrase 40 times.
  const toTranslate = new Set();
  const nodeSources = new Map(); // node -> normalized source
  for (const n of nodes) {
    const src = n.nodeValue.trim();
    // Preserve leading/trailing whitespace when we write back the translation.
    nodeSources.set(n, src);
    if (cache.has(src) || applied.get(n) === src) continue;
    toTranslate.add(src);
  }

  const missing = [...toTranslate].filter((s) => !cache.has(s));
  if (missing.length) {
    // Fire every chunk in parallel so a 500-string page translates in one
    // round-trip instead of ten sequential ones.
    const chunks = [];
    for (let i = 0; i < missing.length; i += MAX_BATCH_STRINGS) {
      chunks.push(missing.slice(i, i + MAX_BATCH_STRINGS));
    }
    const results = await Promise.all(chunks.map((c) => translateBatch(c, lang)));
    for (const out of results) {
      for (const [src, dst] of Object.entries(out)) {
        if (dst && dst !== src) cache.set(src, dst);
      }
    }
    saveCache(lang, cache);
  }

  // Apply translations to the DOM.
  for (const n of nodes) {
    const src = nodeSources.get(n);
    if (!src) continue;
    const dst = cache.get(src);
    if (!dst || dst === src) continue;
    if (applied.get(n) === dst) continue;
    // Preserve leading/trailing whitespace so inline flow stays intact.
    const original = n.nodeValue;
    const leading = original.match(/^\s*/)[0];
    const trailing = original.match(/\s*$/)[0];
    n.nodeValue = leading + dst + trailing;
    applied.set(n, dst);
  }
}

export default function AutoTranslate() {
  const { lang } = useLang() || {};
  const cacheRef = useRef(new Map());
  const appliedRef = useRef(new WeakMap());
  const langRef = useRef(lang);
  const runTimerRef = useRef(null);

  useEffect(() => {
    langRef.current = lang;
    if (!lang || lang === "en") {
      // Reset on switch back to English so future language changes retranslate afresh.
      appliedRef.current = new WeakMap();
      return;
    }
    // Load persisted cache for this language.
    cacheRef.current = loadCache(lang);
    appliedRef.current = new WeakMap();
    // Kick off a first pass on the next paint.
    const first = requestAnimationFrame(() => translateDom(lang, cacheRef.current, appliedRef.current));

    // Debounced observer for React re-renders / route changes.
    const scheduleRun = () => {
      if (runTimerRef.current) clearTimeout(runTimerRef.current);
      runTimerRef.current = setTimeout(() => {
        translateDom(langRef.current, cacheRef.current, appliedRef.current);
      }, 250);
    };
    const observer = new MutationObserver((mutations) => {
      // Ignore mutations we caused ourselves (nodeValue updates).
      for (const m of mutations) {
        if (m.type === "childList" && (m.addedNodes.length || m.removedNodes.length)) {
          scheduleRun();
          return;
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(first);
      observer.disconnect();
      if (runTimerRef.current) clearTimeout(runTimerRef.current);
    };
  }, [lang]);

  return null;
}
